// Place existing review threads on blocks (SPEC §6 "Anchoring comments", "Outdated comments").
import type { DiffItem, FileDiff } from './blockDiff';
import type { ReviewThread } from './api';

export type PlacedThread = ReviewThread & {
  /** Shown collapsed with "Outdated · text changed in <sha>". */
  outdated: boolean;
};

export type ThreadPlacement = {
  /** Index into diff.items → threads shown as a badge on that block. */
  byItem: Map<number, PlacedThread[]>;
  /** Threads that can't be tied to a block (file-level without a Hashquill marker). */
  file: PlacedThread[];
};

const MARKER = /<!--\s*hashquill:\s*file=(\S+)\s+lines=(\d+)-(\d+)\s+sha=([0-9a-f]+)\s*-->/;

export function parseMarker(body: string): { file: string; startLine: number; endLine: number; sha: string } | null {
  const m = MARKER.exec(body);
  return m ? { file: m[1]!, startLine: Number(m[2]), endLine: Number(m[3]), sha: m[4]! } : null;
}

/** The quoted text Hashquill puts under the marker ("> first ~200 chars…"). */
function quoted(body: string): string | null {
  const m = /^>\s?(.+)$/m.exec(body.replace(MARKER, ''));
  return m ? m[1]!.replace(/…$/, '').trim() : null;
}

/** Item showing the block that contains `line` on the given side, or the nearest one before it. */
function itemForLine(items: DiffItem[], line: number, side: 'head' | 'base'): number | null {
  let nearest: number | null = null;
  items.forEach((it, i) => {
    const b = side === 'head' ? ('head' in it ? it.head : undefined) : ('base' in it ? it.base : undefined);
    if (!b) return;
    if (b.startLine <= line) nearest = i;
  });
  return nearest;
}

export function placeThreads(threads: ReviewThread[], path: string, diff: FileDiff): ThreadPlacement {
  const byItem = new Map<number, PlacedThread[]>();
  const file: PlacedThread[] = [];
  const put = (i: number | null, t: PlacedThread) => {
    if (i === null) { file.push(t); return; }
    byItem.set(i, [...(byItem.get(i) ?? []), t]);
  };

  for (const t of threads) {
    if (t.path !== path) continue;
    const first = t.comments[0];
    if (t.subjectType === 'FILE') {
      // FILE threads always report line 1: anchor them by the Hashquill marker instead.
      const marker = first ? parseMarker(first.body) : null;
      if (!marker) { file.push({ ...t, outdated: false }); continue; }
      const i = itemForLine(diff.items, marker.startLine, 'head');
      const q = first ? quoted(first.body) : null;
      const item = i === null ? undefined : diff.items[i];
      const source = item && 'head' in item ? item.head.source.replace(/\s+/g, ' ') : '';
      const outdated = !!q && !source.includes(q.replace(/\s+/g, ' ').slice(0, 60));
      put(i, { ...t, outdated });
      continue;
    }
    if (t.isOutdated) {
      // Collapsed on the nearest block by its original line.
      put(itemForLine(diff.items, t.originalLine ?? 1, t.diffSide === 'LEFT' ? 'base' : 'head'), { ...t, outdated: true });
      continue;
    }
    put(itemForLine(diff.items, t.line ?? t.originalLine ?? 1, t.diffSide === 'LEFT' ? 'base' : 'head'), { ...t, outdated: false });
  }
  return { byItem, file };
}
